'use client'

import { cn } from '@brew/ui/cn'
import { useTranslations } from 'next-intl'
import {
  type ChangeEventHandler,
  type FormEvent,
  type FormEventHandler,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { InputGroup } from '@/components/ui/input-group'
import { useChatFileScope } from '@/hooks/use-chat-file-scope'
import {
  MAX_USER_UPLOAD_FILES,
  validateUserAttachment,
} from '@/lib/core/media/image-upload-support'
import { appToast } from '@/lib/shared/toast'
import {
  type AttachmentsContext,
  isAttachmentDescriptor,
  isUploadedAttachment,
  normalizeAttachmentInput,
  type PromptInputAttachment,
  type PromptInputAttachmentInput,
  type PromptInputAttachmentsState,
  type PromptInputAttachmentUploadBehavior,
  type PromptInputFilePart,
  scheduleAttachmentUploads,
} from './attachments'
import {
  PromptInputDictationProvider,
  usePromptInputDictationController,
} from './dictation-context'
import {
  LocalAttachmentsContext,
  useOptionalPromptInputController,
} from './provider'

export type PromptInputMessage = {
  text?: string
  files?: Array<PromptInputFilePart>
}

export type PromptInputProps = Omit<
  HTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'onError'
> & {
  attachmentUploadBehavior?: PromptInputAttachmentUploadBehavior
  /**
   * Externally owned attachment state, for staged files that must outlive
   * this composer's mount. Omitted, the composer keeps its own. Ignored
   * under a `PromptInputProvider`.
   */
  attachmentsState?: PromptInputAttachmentsState
  clearAttachmentsOnSubmit?: boolean
  accept?: string
  multiple?: boolean
  globalDrop?: boolean
  syncHiddenInput?: boolean
  maxFiles?: number
  maxFileSize?: number
  onError?: (err: {
    code: 'max_files' | 'max_file_size' | 'accept'
    message: string
  }) => void
  /**
   * Return (or resolve) `false` when the send is declined — nothing went
   * out, so the attachments and any dictation in progress stay as they are.
   */
  onSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>
  ) => unknown
  onSubmitStart?: () => void
}

export const PromptInput = ({
  attachmentUploadBehavior = 'upload',
  attachmentsState,
  clearAttachmentsOnSubmit = true,
  className,
  accept,
  multiple,
  globalDrop,
  syncHiddenInput,
  maxFiles,
  maxFileSize,
  onError,
  onSubmit,
  onSubmitStart,
  children,
  ...props
}: PromptInputProps) => {
  const t = useTranslations('chat')
  const controller = useOptionalPromptInputController()
  const isUsingProvider = !!controller
  const dictation = usePromptInputDictationController()

  const inputRef = useRef<HTMLInputElement | null>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const formRef = useRef<HTMLFormElement | null>(null)

  useEffect(() => {
    const root = anchorRef.current?.closest('form')
    if (root instanceof HTMLFormElement) {
      formRef.current = root
    }
  }, [])

  const [ownItems, setOwnItems] = useState<Array<PromptInputAttachment>>([])
  const isExternallyOwned = Boolean(attachmentsState)
  const items = attachmentsState?.files ?? ownItems
  const setItems = attachmentsState?.setFiles ?? setOwnItems
  const settleUpload = attachmentsState?.settleUpload ?? setOwnItems
  const chatFileScope = useChatFileScope()
  const files = isUsingProvider ? controller.attachments.files : items

  const openFileDialogLocal = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const matchesAccept = useCallback(
    (file: File) => {
      if (!accept || accept.trim() === '') {
        return true
      }
      if (accept.includes('image/*')) {
        return file.type.startsWith('image/')
      }
      return true
    },
    [accept]
  )

  const addLocal = useCallback(
    (fileList: Array<PromptInputAttachmentInput> | FileList) => {
      const incoming = Array.from(
        fileList as ArrayLike<PromptInputAttachmentInput>
      )
      const accepted = incoming.filter((input) => {
        if (isUploadedAttachment(input)) {
          return true
        }
        const file = isAttachmentDescriptor(input) ? input.file : input
        const isSyntheticDescriptor =
          isAttachmentDescriptor(input) &&
          Boolean(input.previewText || input.isHtmlAttachment)
        if (!isSyntheticDescriptor) {
          const validation = validateUserAttachment(file)
          if (!validation.ok) {
            appToast.error(validation.reason)
            return false
          }
        }
        return matchesAccept(file)
      })
      if (incoming.length && accepted.length === 0) {
        onError?.({
          code: 'accept',
          message: 'No files match the accepted types.',
        })
        return
      }

      const withinSize = (input: PromptInputAttachmentInput) => {
        if (isUploadedAttachment(input)) {
          return true
        }
        const file = isAttachmentDescriptor(input) ? input.file : input
        return maxFileSize ? file.size <= maxFileSize : true
      }
      const sized = accepted.filter(withinSize)
      if (accepted.length > 0 && sized.length === 0) {
        onError?.({
          code: 'max_file_size',
          message: 'All files exceed the maximum size.',
        })
        return
      }

      const fileLimit = maxFiles ?? MAX_USER_UPLOAD_FILES
      const capacity = Math.max(0, fileLimit - items.length)
      const capped = sized.slice(0, capacity)

      if (sized.length > capacity) {
        onError?.({
          code: 'max_files',
          message: 'Too many files. Some were not added.',
        })
      }

      const normalizedItems = capped.map((file) =>
        normalizeAttachmentInput(file, attachmentUploadBehavior)
      )
      setItems((prev) => prev.concat(normalizedItems))
      scheduleAttachmentUploads({
        items: normalizedItems,
        inputs: capped,
        uploadBehavior: attachmentUploadBehavior,
        update: settleUpload,
        chatFileScope,
      })
    },
    [
      attachmentUploadBehavior,
      chatFileScope,
      items.length,
      matchesAccept,
      maxFileSize,
      maxFiles,
      onError,
      setItems,
      settleUpload,
    ]
  )

  const add = useMemo(
    () =>
      isUsingProvider
        ? (filesToAdd: Array<PromptInputAttachmentInput> | FileList) =>
            controller.attachments.add(filesToAdd)
        : addLocal,
    [isUsingProvider, controller, addLocal]
  )

  const remove = useMemo(
    () =>
      isUsingProvider
        ? (id: string) => controller.attachments.remove(id)
        : (id: string) =>
            setItems((prev) => {
              const found = prev.find((file) => file.id === id)
              if (found?.url?.startsWith('blob:')) {
                URL.revokeObjectURL(found.url)
              }
              return prev.filter((file) => file.id !== id)
            }),
    [isUsingProvider, controller, setItems]
  )

  const clear = useMemo(
    () =>
      isUsingProvider
        ? () => controller.attachments.clear()
        : () =>
            setItems((prev) => {
              for (const file of prev) {
                if (file.url?.startsWith('blob:')) {
                  URL.revokeObjectURL(file.url)
                }
              }
              return []
            }),
    [isUsingProvider, controller, setItems]
  )

  const openFileDialog = useMemo(
    () =>
      isUsingProvider
        ? () => controller.attachments.openFileDialog()
        : openFileDialogLocal,
    [isUsingProvider, controller, openFileDialogLocal]
  )

  useEffect(() => {
    if (!isUsingProvider) {
      return
    }
    controller.__registerFileInput(inputRef, () => inputRef.current?.click())
  }, [isUsingProvider, controller])

  useEffect(() => {
    if (syncHiddenInput && inputRef.current && files.length === 0) {
      inputRef.current.value = ''
    }
  }, [files, syncHiddenInput])

  useEffect(() => {
    const form = formRef.current
    if (!form) {
      return
    }

    const onDragOver = (event: DragEvent) => {
      if (event.dataTransfer?.types?.includes('Files')) {
        event.preventDefault()
      }
    }
    const onDrop = (event: DragEvent) => {
      if (event.dataTransfer?.types?.includes('Files')) {
        event.preventDefault()
      }
      if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
        add(event.dataTransfer.files)
      }
    }

    form.addEventListener('dragover', onDragOver)
    form.addEventListener('drop', onDrop)
    return () => {
      form.removeEventListener('dragover', onDragOver)
      form.removeEventListener('drop', onDrop)
    }
  }, [add])

  useEffect(() => {
    if (!globalDrop) {
      return
    }

    const onDragOver = (event: DragEvent) => {
      if (event.dataTransfer?.types?.includes('Files')) {
        event.preventDefault()
      }
    }
    const onDrop = (event: DragEvent) => {
      if (event.dataTransfer?.types?.includes('Files')) {
        event.preventDefault()
      }
      if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
        add(event.dataTransfer.files)
      }
    }

    document.addEventListener('dragover', onDragOver)
    document.addEventListener('drop', onDrop)
    return () => {
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('drop', onDrop)
    }
  }, [add, globalDrop])

  // Externally owned files outlive this mount; their previews are revoked
  // when an upload settles, the file is removed or cleared, or its owner
  // discards them.
  useEffect(
    () => () => {
      if (!isUsingProvider && !isExternallyOwned) {
        for (const file of files) {
          if (file.url?.startsWith('blob:')) {
            URL.revokeObjectURL(file.url)
          }
        }
      }
    },
    [isUsingProvider, isExternallyOwned, files]
  )

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.currentTarget.files) {
      add(event.currentTarget.files)
    }
  }

  const ctx = useMemo<AttachmentsContext>(
    () => ({
      files: files.map((item) => ({ ...item, id: item.id })),
      add,
      remove,
      clear,
      isUploading: files.some((item) => item.uploadStatus === 'uploading'),
      hasUploadError: files.some((item) => item.uploadStatus === 'error'),
      openFileDialog,
      fileInputRef: inputRef,
    }),
    [files, add, remove, clear, openFileDialog]
  )

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    onSubmitStart?.()

    const form = event.currentTarget
    const text = isUsingProvider
      ? controller.textInput.value
      : (() => {
          const formData = new FormData(form)
          return (formData.get('message') as string) || ''
        })()

    const hasPendingUploads = files.some(
      ({ uploadStatus }) => uploadStatus === 'uploading'
    )
    const hasUploadErrors = files.some(
      ({ uploadStatus }) => uploadStatus === 'error'
    )

    if (hasPendingUploads || hasUploadErrors) {
      return
    }

    // While the send decides (an async send can stash files first), new
    // speech is held back: the prompt stays exactly the text being sent.
    const held = dictation.hold('send')
    const settle = (result: unknown) => {
      if (result === false) {
        // Declined: nothing went out; held speech lands and dictation goes on.
        held.release()
        return
      }
      // Sent: dictation ends, so a late transcript can never refill the
      // cleared box.
      held.cancel()
      if (clearAttachmentsOnSubmit) {
        clear()
      }
    }

    const callOnSubmit = (convertedFiles: Array<PromptInputFilePart>) => {
      try {
        const result = onSubmit({ text, files: convertedFiles }, event)

        if (result instanceof Promise) {
          result.then(settle).catch(() => held.release())
        } else {
          settle(result)
        }
      } catch {
        held.release()
      }
    }

    callOnSubmit(files.map(({ id: _id, ...item }) => item))
  }

  const inner = (
    <>
      <span aria-hidden="true" className="hidden" ref={anchorRef} />
      <input
        accept={accept}
        aria-label={t('attachments.upload')}
        className="hidden"
        multiple={multiple}
        onChange={handleChange}
        ref={inputRef}
        title={t('attachments.upload')}
        type="file"
      />
      <form
        className={cn('w-full', className)}
        onSubmit={handleSubmit}
        {...props}
      >
        <InputGroup>{children}</InputGroup>
      </form>
    </>
  )

  const withDictation = (
    <PromptInputDictationProvider value={dictation}>
      {inner}
    </PromptInputDictationProvider>
  )

  return isUsingProvider ? (
    withDictation
  ) : (
    <LocalAttachmentsContext.Provider value={ctx}>
      {withDictation}
    </LocalAttachmentsContext.Provider>
  )
}
